/**
 * Entity Manager — React Query hooks + API helpers.
 * Pattern follows features/asset/api.ts
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cpFetch } from '@/lib/cpClient';
import { useAuthStore } from '@/store/authStore';
import { useSelectedAppId } from '@/hooks/useSelectedAppId';
import { resolveIdentitySessionTenant } from './identitySessionTenant';
import {
  applyIdentityListFilters,
  buildIdentityListRequestParams,
  defaultIdentityListQuery,
} from './identityListQuery';
import type {
  Entity,
  EntityStats,
  EntityFilters,
  CreateEntityPayload,
  UpdateEntityPayload,
  Contact,
  CreateContactPayload,
  EntityAppLink,
  EntityTypeInfo,
  PipelineStatus,
  IdentityWorkflowResult,
} from './types';

/* ================================================================== */
/*  Core Entity hooks                                                   */
/* ================================================================== */

/** Paginated + filtered entity list */
export function useEntities(filters: EntityFilters = {}) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  return useQuery({
    queryKey: ['entities', 'list', appId, filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.page) params.set('page', String(filters.page));
      if (filters.limit) params.set('limit', String(filters.limit));
      if (filters.entity_type) params.set('entity_type', filters.entity_type);
      if (filters.kind) params.set('kind', filters.kind);
      if (filters.status) params.set('status', filters.status);
      if (filters.search) params.set('search', filters.search);
      if (filters.sort_by) params.set('sort_by', filters.sort_by);
      if (filters.sort_order) params.set('sort_order', filters.sort_order);
      const qs = params.toString();
      const res = await cpFetch(`/entity/entities${qs ? `?${qs}` : ''}`);
      const entities: Entity[] = res?.data ?? res?.entities ?? [];
      const total: number = res?.pagination?.total ?? entities.length;
      return {
        entities,
        total,
        page: res?.pagination?.page ?? 1,
        limit: res?.pagination?.limit ?? 20,
        pages: res?.pagination?.pages ?? 1,
      };
    },
    enabled: !!token && !!appId,
    staleTime: 15_000,
  });
}

/** Party-family entities only (legal entity · natural person · brand) — for owner / legal-entity pickers. */
export function usePartyEntities(filters: Omit<EntityFilters, 'kind'> = {}) {
  const partyQuery = defaultIdentityListQuery();
  const query = useEntities({ ...filters, kind: buildIdentityListRequestParams(partyQuery).kind });
  const data = query.data
    ? { ...query.data, entities: applyIdentityListFilters(query.data.entities, partyQuery) }
    : query.data;
  return { ...query, data };
}

/** Single entity by ID */
export function useEntity(id: string | undefined) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  return useQuery({
    queryKey: ['entities', 'detail', appId, id],
    queryFn: async () => {
      const res = await cpFetch(`/entity/entities/${id}`);
      return (res?.data ?? res?.entity ?? res) as Entity;
    },
    enabled: !!token && !!appId && !!id,
    staleTime: 10_000,
  });
}

/** Dashboard stats */
export function useEntityStats() {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  return useQuery<EntityStats>({
    queryKey: ['entities', 'stats', appId],
    queryFn: async () => {
      const res = await cpFetch('/entity/stats');
      return res?.stats ?? res;
    },
    enabled: !!token && !!appId,
    staleTime: 30_000,
  });
}

/** Entity types */
export function useEntityTypes() {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  return useQuery<EntityTypeInfo[]>({
    queryKey: ['entities', 'types', appId],
    queryFn: async () => {
      const res = await cpFetch('/entity/types');
      return res?.types ?? [];
    },
    enabled: !!token && !!appId,
    staleTime: 300_000,
  });
}

/** Create entity */
export function useCreateEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateEntityPayload) => {
      const res = await cpFetch('/entity/entities', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return (res?.data ?? res?.entity ?? res) as Entity;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entities'] });
    },
  });
}

/** Update entity */
export function useUpdateEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: UpdateEntityPayload & { id: string }) => {
      const res = await cpFetch(`/entity/entities/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return (res?.data ?? res?.entity ?? res) as Entity;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['entities'] });
      qc.invalidateQueries({ queryKey: ['entities', 'detail', vars.id] });
    },
  });
}

/** Delete (soft) entity */
export function useDeleteEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await cpFetch(`/entity/entities/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entities'] });
    },
  });
}

/* ================================================================== */
/*  PDF Pipeline hooks                                                  */
/* ================================================================== */

/** Upload PDF to create entity */
export function useCreateEntityFromPdf() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, entity_type }: { file: File; entity_type?: string }) => {
      const formData = new FormData();
      formData.append('file', file);
      if (entity_type) formData.append('entity_type', entity_type);
      const res = await cpFetch('/entity/entities/create-from-pdf', {
        method: 'POST',
        body: formData,
        headers: {}, // Let browser set Content-Type for multipart
      });
      return res as { success: boolean; pipeline_id: string; entity_id: string };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entities'] });
    },
  });
}

/** Pipeline status for entity */
export function useEntityPipelineStatus(entityId: string | undefined) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  return useQuery<PipelineStatus>({
    queryKey: ['entities', 'pipeline', appId, entityId],
    queryFn: async () => {
      const res = await cpFetch(`/entity/entities/${entityId}/status`);
      return res?.data ?? res;
    },
    enabled: !!token && !!appId && !!entityId,
    // Poll while still processing, stop once the pipeline finishes.
    refetchInterval: (data) => (data?.status === 'processing' ? 3000 : false),
    staleTime: 1000,
  });
}

/** All pipeline statuses */
export function useEntityPipelines() {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  return useQuery<PipelineStatus[]>({
    queryKey: ['entities', 'pipelines', appId],
    queryFn: async () => {
      const res = await cpFetch('/entity/pipeline-status');
      return res?.data ?? [];
    },
    enabled: !!token && !!appId,
    staleTime: 10_000,
  });
}

/* ================================================================== */
/*  Contact hooks                                                       */
/* ================================================================== */

/** Contacts who can sign in to the selected app (paginated + filterable) */
export function useAllContacts(filters: { search?: string; role?: string; status?: string; entity_id?: string; page?: number; limit?: number } = {}) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  return useQuery({
    queryKey: ['contacts', 'all', appId, filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (appId) params.set('app_id', appId);
      if (filters.search) params.set('search', filters.search);
      if (filters.role) params.set('role', filters.role);
      if (filters.status) params.set('status', filters.status);
      if (filters.entity_id) params.set('entity_id', filters.entity_id);
      if (filters.page) params.set('page', String(filters.page));
      if (filters.limit) params.set('limit', String(filters.limit || 50));
      const qs = params.toString();
      const res = await cpFetch(`/entity/contacts${qs ? `?${qs}` : ''}`);
      return res as {
        data: (Contact & {
          entity_name?: string;
          entity_type?: string;
          app_role?: { role: string; via: 'app_role' | 'entity_link' } | null;
        })[];
        total: number;
        page: number;
        limit: number;
        pages: number;
        scoped_app_id?: string;
      };
    },
    enabled: !!token && !!appId,
    staleTime: 15_000,
  });
}

/** Contacts for an entity */
export function useEntityContacts(entityId: string | undefined) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  return useQuery<Contact[]>({
    queryKey: ['entities', 'contacts', appId, entityId],
    queryFn: async () => {
      const res = await cpFetch(`/entity/entities/${entityId}/contacts`);
      return res?.data ?? res?.contacts ?? [];
    },
    enabled: !!token && !!appId && !!entityId,
    staleTime: 15_000,
  });
}

/** Add contact to entity */
export function useCreateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ entityId, ...payload }: CreateContactPayload & { entityId: string }) => {
      const res = await cpFetch(`/entity/entities/${entityId}/contacts`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const contact = (res?.data ?? res?.contact ?? res) as Contact;
      return { ...contact, attached: res?.attached === true };
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['entities', 'contacts', vars.entityId] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

/** Update contact */
export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: Partial<Contact> & { id: string }) => {
      const res = await cpFetch(`/entity/contacts/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return (res?.data ?? res?.contact ?? res) as Contact;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entities', 'contacts'] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

/** Delete contact */
export function useDeleteContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await cpFetch(`/entity/contacts/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entities', 'contacts'] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

/** Set app roles on a contact */
export function useSetContactAppRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ contactId, app_id, role }: { contactId: string; app_id: string; role: string }) => {
      const res = await cpFetch(`/entity/contacts/${contactId}/app-roles`, {
        method: 'PUT',
        body: JSON.stringify({ app_id, role }),
      });
      return (res?.data ?? res?.contact ?? res) as Contact;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entities', 'contacts'] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
    },
  });
}

/* ================================================================== */
/*  App Link hooks                                                      */
/* ================================================================== */

/** Apps linked to an entity */
export function useEntityAppLinks(entityId: string | undefined) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  return useQuery<EntityAppLink[]>({
    queryKey: ['entities', 'app-links', appId, entityId],
    queryFn: async () => {
      const res = await cpFetch(`/entity/entities/${entityId}/apps`);
      return res?.data ?? res?.app_links ?? [];
    },
    enabled: !!token && !!appId && !!entityId,
    staleTime: 15_000,
  });
}

/** Link entity to app */
export function useLinkEntityToApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ entityId, app_id, app_metadata }: { entityId: string; app_id: string; app_metadata?: Record<string, any> }) => {
      const res = await cpFetch(`/entity/entities/${entityId}/apps`, {
        method: 'POST',
        body: JSON.stringify({ app_id, app_metadata }),
      });
      return (res?.data ?? res?.app_link ?? res) as EntityAppLink;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['entities', 'app-links', vars.entityId] });
    },
  });
}

/** Unlink entity from app */
export function useUnlinkEntityFromApp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ entityId, app_id }: { entityId: string; app_id: string }) => {
      await cpFetch(`/entity/entities/${entityId}/apps/${app_id}`, { method: 'DELETE' });
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['entities', 'app-links', vars.entityId] });
    },
  });
}

/* ================================================================== */
/*  Identity Agent workflow execution                                   */
/* ================================================================== */

/** Run an identity agent workflow via Volcano */
export function useRunIdentityWorkflow() {
  const qc = useQueryClient();
  const selectedTenant = useAuthStore((s) => s.selectedTenant);
  const tokenTenant = useAuthStore((s) => s.payload?.tid || s.payload?.tenantId || s.payload?.tenant);
  return useMutation({
    mutationFn: async ({ workflow, input }: { workflow: string; input: Record<string, any> }) => {
      const tenant_id = resolveIdentitySessionTenant({ selectedTenant, tokenTenant });
      if (!tenant_id) throw new Error('Missing tenant context');
      const res = await cpFetch('/api/volcano/execute', {
        method: 'POST',
        body: JSON.stringify({
          agent_id: 'identity',
          workflow,
          input,
          tenant_id,
        }),
      });
      return res as IdentityWorkflowResult;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts'] });
      qc.invalidateQueries({ queryKey: ['entities'] });
    },
  });
}

/* ================================================================== */
/*  Platform Apps hooks                                                 */
/* ================================================================== */

/** List all platform apps (for role assignment) */
export function useAllApps() {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  return useQuery({
    queryKey: ['platform-apps', appId],
    queryFn: async () => {
      const res = await cpFetch('/api/apps');
      const apps = res?.apps ?? res?.data ?? [];
      return apps.filter((a: any) => a.status === 'active') as Array<{ app_id: string; name: string; status: string }>;
    },
    enabled: !!token && !!appId,
    staleTime: 60_000,
  });
}

/** Bulk import entities */
export function useBulkImportEntities() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entities: CreateEntityPayload[]) => {
      const res = await cpFetch('/entity/entities/bulk-import', {
        method: 'POST',
        body: JSON.stringify({ entities }),
      });
      return res;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entities'] });
    },
  });
}
